import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  List,
  ListItem,
  ListItemText,
  Collapse,
  IconButton,
  Divider,
  ListSubheader,
  Grid,
  Button,
  Card,
  CardContent,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  Search as SearchIcon,
  Download as DownloadIcon,
  Google as GoogleIcon,
} from '@mui/icons-material';
import axios from 'axios';
import CircularProgress from '@mui/material/CircularProgress';
import ButtonGroup from '@mui/material/ButtonGroup';
import Logger from '../utils/logger';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

function getOrdinalSuffix(number) {
  const j = number % 10;
  const k = number % 100;
  if (j === 1 && k !== 11) {
    return number + "st";
  }
  if (j === 2 && k !== 12) {
    return number + "nd";
  }
  if (j === 3 && k !== 13) {
    return number + "rd";
  }
  return number + "th";
}

function WorksheetGenerator({ user, onAuthRequired }) {
  const [k8Grades, setK8Grades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('Science');
  const [standards, setStandards] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [searchResults, setSearchResults] = useState(null);
  const [worksheetRequirements, setWorksheetRequirements] = useState('');
  const [generatedWorksheet, setGeneratedWorksheet] = useState(null);
  const [selectedStandard, setSelectedStandard] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [followUpRequirements, setFollowUpRequirements] = useState('');
  const [selectedCoreIdea, setSelectedCoreIdea] = useState(null);
  const [selectedStudentObjectives, setSelectedStudentObjectives] = useState([]);
  const [worksheetHistory, setWorksheetHistory] = useState([]);
  const [currentVersionIndex, setCurrentVersionIndex] = useState(-1);
  const [hasPromptedForAuth, setHasPromptedForAuth] = useState(false);
  const [generationCount, setGenerationCount] = useState(0);

  useEffect(() => {
    // Load grade levels and subjects
    Logger.info('Loading grade levels and subjects');
    axios.get(`${API_BASE_URL}/grade-levels`)
      .then(response => {
        setK8Grades(response.data.k8_grades);
        setSubjects(response.data.subjects);
        Logger.debug('Grade levels and subjects loaded', { grades: response.data.k8_grades, subjects: response.data.subjects });
      })
      .catch(error => Logger.apiError('grade-levels', error));
  }, []);

  useEffect(() => {
    if (selectedGrade) {
      Logger.info(`Loading standards for grade: ${selectedGrade}`);
      axios.get(`${API_BASE_URL}/standards/${selectedGrade}`)
        .then(response => {
          setStandards(response.data);
          Logger.debug(`Standards loaded for grade ${selectedGrade}`, { standards: response.data });
        })
        .catch(error => Logger.apiError(`standards/${selectedGrade}`, error));
    }
  }, [selectedGrade]);

  // Load saved state from localStorage when component mounts
  useEffect(() => {
    Logger.debug('Loading saved state from localStorage');
    
    // Load saved worksheet history
    const savedHistory = localStorage.getItem('worksheetHistory');
    if (savedHistory) {
      try {
        const parsedHistory = JSON.parse(savedHistory);
        setWorksheetHistory(parsedHistory);
        Logger.debug('Worksheet history loaded', { history: parsedHistory });
      } catch (e) {
        Logger.error('Error parsing saved worksheet history:', e);
      }
    }

    // Load current version index
    const savedVersionIndex = localStorage.getItem('currentVersionIndex');
    if (savedVersionIndex) {
      setCurrentVersionIndex(parseInt(savedVersionIndex, 10));
      Logger.debug('Current version index loaded', { index: savedVersionIndex });
    }

    // Load current worksheet
    const savedWorksheet = localStorage.getItem('currentWorksheet');
    if (savedWorksheet) {
      try {
        const parsedWorksheet = JSON.parse(savedWorksheet);
        setGeneratedWorksheet(parsedWorksheet);
        Logger.debug('Current worksheet loaded', { worksheet: parsedWorksheet });
      } catch (e) {
        Logger.error('Error parsing saved worksheet:', e);
      }
    }

    // Load selected standard
    const savedStandard = localStorage.getItem('selectedStandard');
    if (savedStandard) {
      try {
        const parsedStandard = JSON.parse(savedStandard);
        setSelectedStandard(parsedStandard);
        Logger.debug('Selected standard loaded', { standard: parsedStandard });
      } catch (e) {
        Logger.error('Error parsing saved standard:', e);
      }
    }

    // Load selected core idea
    const savedCoreIdea = localStorage.getItem('selectedCoreIdea');
    if (savedCoreIdea) {
      try {
        const parsedCoreIdea = JSON.parse(savedCoreIdea);
        setSelectedCoreIdea(parsedCoreIdea);
        Logger.debug('Selected core idea loaded', { coreIdea: parsedCoreIdea });
      } catch (e) {
        Logger.error('Error parsing saved core idea:', e);
      }
    }

    // Load selected student objectives
    const savedObjectives = localStorage.getItem('selectedStudentObjectives');
    if (savedObjectives) {
      try {
        const parsedObjectives = JSON.parse(savedObjectives);
        setSelectedStudentObjectives(parsedObjectives);
        Logger.debug('Selected student objectives loaded', { objectives: parsedObjectives });
      } catch (e) {
        Logger.error('Error parsing saved objectives:', e);
      }
    }

    // Load worksheet requirements
    const savedRequirements = localStorage.getItem('worksheetRequirements');
    if (savedRequirements) {
      setWorksheetRequirements(savedRequirements);
      Logger.debug('Worksheet requirements loaded', { requirements: savedRequirements });
    }

    // Load follow-up requirements
    const savedFollowUp = localStorage.getItem('followUpRequirements');
    if (savedFollowUp) {
      setFollowUpRequirements(savedFollowUp);
      Logger.debug('Follow-up requirements loaded', { requirements: savedFollowUp });
    }
  }, []);

  // Save state to localStorage when it changes
  useEffect(() => {
    if (worksheetHistory.length > 0) {
      localStorage.setItem('worksheetHistory', JSON.stringify(worksheetHistory));
      Logger.debug('Worksheet history saved', { history: worksheetHistory });
    }
  }, [worksheetHistory]);

  useEffect(() => {
    localStorage.setItem('currentVersionIndex', currentVersionIndex.toString());
    Logger.debug('Current version index saved', { index: currentVersionIndex });
  }, [currentVersionIndex]);

  useEffect(() => {
    if (generatedWorksheet) {
      localStorage.setItem('currentWorksheet', JSON.stringify(generatedWorksheet));
      Logger.debug('Current worksheet saved', { worksheet: generatedWorksheet });
    }
  }, [generatedWorksheet]);

  useEffect(() => {
    if (selectedStandard) {
      localStorage.setItem('selectedStandard', JSON.stringify(selectedStandard));
      Logger.debug('Selected standard saved', { standard: selectedStandard });
    }
  }, [selectedStandard]);

  useEffect(() => {
    if (selectedCoreIdea) {
      localStorage.setItem('selectedCoreIdea', JSON.stringify(selectedCoreIdea));
      Logger.debug('Selected core idea saved', { coreIdea: selectedCoreIdea });
    }
  }, [selectedCoreIdea]);

  useEffect(() => {
    if (selectedStudentObjectives.length > 0) {
      localStorage.setItem('selectedStudentObjectives', JSON.stringify(selectedStudentObjectives));
      Logger.debug('Selected student objectives saved', { objectives: selectedStudentObjectives });
    }
  }, [selectedStudentObjectives]);

  useEffect(() => {
    if (worksheetRequirements) {
      localStorage.setItem('worksheetRequirements', worksheetRequirements);
    }
  }, [worksheetRequirements]);

  useEffect(() => {
    if (followUpRequirements) {
      localStorage.setItem('followUpRequirements', followUpRequirements);
    }
  }, [followUpRequirements]);

  // Add a function to clear saved data
  const clearSavedData = () => {
    localStorage.removeItem('worksheetHistory');
    localStorage.removeItem('currentVersionIndex');
    localStorage.removeItem('currentWorksheet');
    localStorage.removeItem('selectedStandard');
    localStorage.removeItem('selectedCoreIdea');
    localStorage.removeItem('selectedStudentObjectives');
    localStorage.removeItem('worksheetRequirements');
    localStorage.removeItem('followUpRequirements');
    
    // Reset state
    setWorksheetHistory([]);
    setCurrentVersionIndex(-1);
    setGeneratedWorksheet(null);
    setSelectedStandard(null);
    setSelectedCoreIdea(null);
    setSelectedStudentObjectives([]);
    setWorksheetRequirements('');
    setFollowUpRequirements('');
  };

  const handleSearch = () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }

    if (!selectedGrade) {
      Logger.warn('Search attempted without grade selection');
      setError('Please select a grade level first');
      return;
    }

    try {
      Logger.info(`Searching for: ${searchQuery} in loaded standards`);
      const searchLower = searchQuery.toLowerCase();
      const results = {};

      // Search through loaded standards
      Object.entries(standards).forEach(([key, standard]) => {
        let isMatch = false;

        // Search in standard title
        if (standard.title.toLowerCase().includes(searchLower)) {
          isMatch = true;
        }

        // Search in disciplinary core ideas
        if (!isMatch && standard.disciplinary_core_ideas) {
          Object.values(standard.disciplinary_core_ideas).forEach(idea => {
            if (idea.title.toLowerCase().includes(searchLower)) {
              isMatch = true;
            }
            // Search in bullet points
            if (!isMatch && idea.bullet_points) {
              idea.bullet_points.forEach(bullet => {
                if (bullet.statement && bullet.statement.toLowerCase().includes(searchLower)) {
                  isMatch = true;
                }
              });
            }
          });
        }

        // Search in student objectives
        if (!isMatch && standard.student_objectives) {
          Object.values(standard.student_objectives).forEach(objective => {
            if (typeof objective === 'string' && objective.toLowerCase().includes(searchLower)) {
              isMatch = true;
            }
          });
        }

        if (isMatch) {
          results[key] = standard;
        }
      });

      setSearchResults(results);
      Logger.debug('Search results:', results);
      
      // Automatically expand all standards in search results
      const expandedSet = new Set(Object.keys(results));
      setExpandedItems(expandedSet);
    } catch (error) {
      Logger.error('Search error:', error);
      setError('Failed to search standards');
    }
  };

  const highlightText = (text) => {
    if (!searchQuery || !text) return text;
    const parts = text.split(new RegExp(`(${searchQuery})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === searchQuery.toLowerCase() ? 
        <Typography key={index} component="span" sx={{ 
          backgroundColor: '#ffeb3b', 
          padding: '0 2px', 
          borderRadius: '2px',
          fontWeight: 'bold'
        }}>
          {part}
        </Typography> : 
        part
    );
  };

  const handleStandardSelect = (key, value) => {
    Logger.userAction('Standard selected', { key, value });
    setSelectedStandard({ id: key, ...value });
    toggleExpand(key);
  };

  const handleCoreIdeaSelect = (standardKey, coreKey, coreData) => {
    Logger.userAction('Core idea selected', { standardKey, coreKey, coreData });
    
    // Set the selected standard
    setSelectedStandard({
      id: standardKey,
      title: standards[standardKey].title,
      disciplinary_core_ideas: standards[standardKey].disciplinary_core_ideas
    });
    
    // Set the selected core idea
    setSelectedCoreIdea({
      standardKey,
      coreKey,
      ...coreData
    });
    
    // Get related student objectives
    const objectives = standards[standardKey]?.student_objectives || {};
    const relatedObjectives = Object.entries(objectives)
      .filter(([key]) => coreData.bullet_points.some(bullet => 
        bullet.student_can_do_mappings.includes(key)
      ))
      .map(([key, value]) => ({ key, ...value }));
    
    setSelectedStudentObjectives(relatedObjectives);
    Logger.debug('Related objectives loaded', { objectives: relatedObjectives });
  };

  const handleGenerateWorksheet = async (standard, requirements, isRevision = false, followUpRequirements = '') => {
    if (!standard) {
      Logger.warn('Worksheet generation attempted without standard selection');
      setError('Please select a standard first');
      return;
    }

    // Increment generation count
    setGenerationCount(prevCount => prevCount + 1);
    
    // If user is anonymous and we should prompt for auth (first time or every 4 generations)
    if (user?.isAnonymous && onAuthRequired && (generationCount === 0 || generationCount % 4 === 0)) {
      Logger.info('Prompting anonymous user to sign in');
      onAuthRequired();
      return;
    }

    setError(null);
    setIsGenerating(true);
    Logger.info(`Generating ${isRevision ? 'revision' : 'new'} worksheet`, { standard, requirements });
    
    try {
      // Create a context from the selected core idea and related objectives
      const context = {
        standard_id: standard.id,
        standard_title: standard.title,
        core_idea_title: selectedCoreIdea.title,
        core_idea_content: selectedCoreIdea.bullet_points.map(bp => bp.statement).join('\n'),
        student_objectives: selectedStudentObjectives.map(obj => 
          `Objective: ${obj.objective}\nClarification: ${obj.clarification_statement}`
        ).join('\n\n'),
        requirements: requirements,
        is_revision: isRevision,
        previous_worksheet: isRevision ? {
          title: generatedWorksheet.title,
          objective: generatedWorksheet.objective,
          content: generatedWorksheet.content
        } : undefined,
        follow_up_requirements: isRevision ? followUpRequirements : undefined,
        grade: selectedGrade
      };

      Logger.debug('Sending worksheet generation request', { context });
      const response = await axios.post(`${API_BASE_URL}/generate-worksheet`, {
        context: context
      });

      const worksheetData = response.data;
      const newWorksheet = {
        ...worksheetData,
        images: worksheetData.images.map(base64 => `data:image/png;base64,${base64}`)
      };

      // Add to version history if it's a revision
      if (isRevision) {
        setWorksheetHistory(prev => [...prev, generatedWorksheet]);
        setCurrentVersionIndex(prev => prev + 1);
        Logger.info('Worksheet revision added to history');
      } else {
        // Reset history for new worksheets
        setWorksheetHistory([]);
        setCurrentVersionIndex(-1);
        Logger.info('New worksheet started, history reset');
      }

      setGeneratedWorksheet(newWorksheet);
      Logger.info('Worksheet generated successfully', { worksheet: newWorksheet });
    } catch (error) {
      Logger.apiError('generate-worksheet', error);
      setError(error.response?.data?.error || 'Failed to generate worksheet');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async (format) => {
    try {
      setError(null);
      Logger.info(`Downloading worksheet as ${format}`);
      
      // Prepare worksheet data by removing data URL prefixes from images
      const worksheetData = {
        ...generatedWorksheet,
        images: generatedWorksheet.images.map(img => 
          img.startsWith('data:image') ? img : `data:image/png;base64,${img}`
        )
      };

      if (format === 'google-docs') {
        Logger.info('Exporting to Google Docs...');
        Logger.debug('Worksheet data for Google Docs:', worksheetData);
        
        const response = await axios.post(
          `${API_BASE_URL}/export-to-google-docs`,
          { worksheet: worksheetData }
        );
        
        Logger.debug('Google Docs response:', response.data);
        
        if (response.data.url) {
          Logger.info('Opening Google Doc:', response.data.url);
          window.open(response.data.url, '_blank');
        } else if (response.data.error) {
          Logger.error('Error in response:', response.data.error);
          setError(response.data.error);
        } else {
          Logger.error('No URL in response:', response.data);
          setError('Failed to get Google Docs URL');
        }
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/download-worksheet`,
        {
          worksheet: worksheetData,
          format: format
        },
        {
          responseType: 'blob'
        }
      );

      // Check if the response is an error message
      if (response.data.type === 'application/json') {
        const reader = new FileReader();
        reader.onload = () => {
          const error = JSON.parse(reader.result);
          Logger.error('Download error:', error);
          setError(error.error || 'Failed to download worksheet');
        };
        reader.readAsText(response.data);
        return;
      }

      // Create and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `worksheet.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      Logger.info(`Worksheet downloaded as ${format}`);
    } catch (error) {
      Logger.apiError('download-worksheet', error);
      const errorMessage = error.response?.data?.error || 
                          error.message || 
                          `Failed to ${format === 'google-docs' ? 'export to Google Docs' : `download worksheet as ${format.toUpperCase()}`}`;
      setError(errorMessage);
    }
  };

  const toggleExpand = (itemId) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const renderStandardsList = (standardsData) => {
    if (!standardsData || Object.keys(standardsData).length === 0) {
      return (
        <ListItem>
          <ListItemText primary="No standards found" />
        </ListItem>
      );
    }

    return Object.entries(standardsData).map(([key, value]) => {
      // Filter core ideas based on search query
      const matchingCoreIdeas = Object.entries(value.disciplinary_core_ideas).filter(([_, coreData]) => {
        if (!searchQuery) return true;
        const searchLower = searchQuery.toLowerCase();
        return (
          coreData.title.toLowerCase().includes(searchLower) ||
          coreData.bullet_points.some(bp => 
            bp.statement.toLowerCase().includes(searchLower)
          )
        );
      });

      // If there's a search query and no matching core ideas, don't show this standard
      if (searchQuery && matchingCoreIdeas.length === 0) {
        return null;
      }

      return (
        <React.Fragment key={key}>
          <ListItem 
            button 
            onClick={() => toggleExpand(key)}
            selected={selectedStandard?.id === key}
          >
            <ListItemText 
              primary={highlightText(value.title)}
              secondary={`Standard: ${highlightText(key)}`}
            />
            <IconButton size="small">
              {expandedItems.has(key) ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </ListItem>
          <Collapse in={expandedItems.has(key)} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {matchingCoreIdeas.map(([coreKey, coreData]) => (
                <ListItem
                  key={coreKey}
                  button
                  selected={selectedCoreIdea?.coreKey === coreKey}
                  onClick={() => handleCoreIdeaSelect(key, coreKey, coreData)}
                  sx={{ pl: 4 }}
                >
                  <ListItemText
                    primary={highlightText(coreData.title)}
                    secondary={
                      <React.Fragment>
                        <Typography component="span" variant="body2" color="text.primary">
                          {highlightText(coreData.bullet_points[0]?.statement)}
                        </Typography>
                        {selectedCoreIdea?.coreKey === coreKey && selectedStudentObjectives.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="subtitle2" color="primary">
                              Related Student Objectives:
                            </Typography>
                            {selectedStudentObjectives.map((obj, index) => (
                              <Typography key={index} variant="body2" sx={{ ml: 2 }}>
                                • {highlightText(obj.objective)}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </React.Fragment>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </React.Fragment>
      );
    }).filter(Boolean); // Remove null entries
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* <Typography variant="h4" component="h1" gutterBottom>
        Create Science and Math Worksheets for Students
      </Typography> */}

      <Grid container spacing={3}>
        {/* Left Panel - Standards Browser */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '80vh', overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              Standards
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 4, mb: 4 }}>
              <FormControl sx={{ minWidth: 160 }}>
                <InputLabel id="grade-select-label">Select a Grade</InputLabel>
                <Select
                  labelId="grade-select-label"
                  value={selectedGrade}
                  onChange={(e) => setSelectedGrade(e.target.value)}
                  label="Select a Grade"
                  sx={{ 
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.87)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    }
                  }}
                >
                  <MenuItem value="" disabled>Select grade</MenuItem>
                  {k8Grades.map(grade => (
                    <MenuItem key={grade} value={grade}>
                      {grade === 'K' ? 'K' : getOrdinalSuffix(grade)}
                    </MenuItem>
                  ))}
                  <MenuItem value="9-12_biology">9-12 Life Science</MenuItem>
                  <MenuItem value="9-12_physics">9-12 Physical Science</MenuItem>
                  <MenuItem value="9-12_earth_science">9-12 Earth and Space Science</MenuItem>
                  <MenuItem value="9-12_engineering">9-12 Engineering Design</MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Subject</InputLabel>
                <Select
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                  label="Subject"
                >
                  <MenuItem value="Science">Science</MenuItem>
                  <MenuItem value="Math">Math</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search core ideas..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                helperText="Search in core ideas and their descriptions"
                sx={{ maxWidth: '300px' }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                sx={{ 
                  bgcolor: 'black', 
                  '&:hover': { bgcolor: '#333' },
                  height: '40px',
                  minWidth: 'auto',
                  px: 2
                }}
              >
                Search
              </Button>
            </Box>

            <List>
              {searchResults ? renderStandardsList(searchResults) : renderStandardsList(standards)}
            </List>
          </Paper>
        </Grid>

        {/* Right Panel - Worksheet Generator */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '80vh', overflow: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Generated Worksheet
              </Typography>
              {generatedWorksheet && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setGeneratedWorksheet(null);
                    // Clear all local storage items
                    localStorage.removeItem('worksheetHistory');
                    localStorage.removeItem('currentVersionIndex');
                    localStorage.removeItem('currentWorksheet');
                    localStorage.removeItem('selectedStandard');
                    localStorage.removeItem('selectedCoreIdea');
                    localStorage.removeItem('selectedStudentObjectives');
                    localStorage.removeItem('worksheetRequirements');
                    localStorage.removeItem('followUpRequirements');
                    
                    // Reset state variables
                    setWorksheetHistory([]);
                    setCurrentVersionIndex(-1);
                    setSelectedStandard(null);
                    setSelectedCoreIdea(null);
                    setSelectedStudentObjectives([]);
                    setWorksheetRequirements('');
                    setFollowUpRequirements('');
                  }}
                  size="small"
                  sx={{ color: 'black', borderColor: 'black', '&:hover': { borderColor: '#333', color: '#333' } }}
                >
                  Create Another
                </Button>
              )}
            </Box>

            {generatedWorksheet ? (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {generatedWorksheet.title}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    Objective: {generatedWorksheet.objective}
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    {generatedWorksheet.content.map((item, idx) => (
                      <Typography key={idx} paragraph>
                        {typeof item === 'string' ? item : (
                          <Box>
                            <Typography variant="subtitle1" gutterBottom>
                              {item.question || item.task}
                            </Typography>
                            {item.description && (
                              <Typography variant="body2" color="text.secondary" paragraph>
                                {item.description}
                              </Typography>
                            )}
                            {item.options && (
                              <Box component="ul" sx={{ pl: 2 }}>
                                {item.options.map((option, optIdx) => (
                                  <Typography key={optIdx} component="li">
                                    {option}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                            {item.correct_answer && (
                              <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                                Answer: {item.correct_answer}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Typography>
                    ))}
                  </Box>

                  {/* Follow-up requirements bar */}
                  <Box sx={{ mt: 3, mb: 2 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      variant="outlined"
                      placeholder="Add follow-up requirements..."
                      value={followUpRequirements}
                      onChange={(e) => setFollowUpRequirements(e.target.value)}
                      sx={{ mb: 1 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleGenerateWorksheet(selectedStandard, worksheetRequirements, true, followUpRequirements)}
                        disabled={!followUpRequirements.trim() || isGenerating}
                        sx={{ bgcolor: 'black', '&:hover': { bgcolor: '#333' } }}
                      >
                        {isGenerating ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={20} color="inherit" />
                            Revising...
                          </Box>
                        ) : (
                          'Revise'
                        )}
                      </Button>
                    </Box>
                  </Box>

                  {/* Action buttons */}
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <ButtonGroup variant="contained" size="small">
                      <Button
                        onClick={() => handleDownload('google-docs')}
                        startIcon={<GoogleIcon />}
                        size="small"
                        sx={{ bgcolor: 'black', '&:hover': { bgcolor: '#333' } }}
                      >
                        Google Docs
                      </Button>
                      <Button
                        onClick={() => handleDownload('docx')}
                        startIcon={<DownloadIcon />}
                        size="small"
                        sx={{ bgcolor: 'black', '&:hover': { bgcolor: '#333' } }}
                      >
                        Microsoft Word
                      </Button>
                    </ButtonGroup>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setGeneratedWorksheet(null);
                        // Clear all local storage items
                        localStorage.removeItem('worksheetHistory');
                        localStorage.removeItem('currentVersionIndex');
                        localStorage.removeItem('currentWorksheet');
                        localStorage.removeItem('selectedStandard');
                        localStorage.removeItem('selectedCoreIdea');
                        localStorage.removeItem('selectedStudentObjectives');
                        localStorage.removeItem('worksheetRequirements');
                        localStorage.removeItem('followUpRequirements');
                        
                        // Reset state variables
                        setWorksheetHistory([]);
                        setCurrentVersionIndex(-1);
                        setSelectedStandard(null);
                        setSelectedCoreIdea(null);
                        setSelectedStudentObjectives([]);
                        setWorksheetRequirements('');
                        setFollowUpRequirements('');
                      }}
                      size="small"
                      sx={{ color: 'black', borderColor: 'black', '&:hover': { borderColor: '#333', color: '#333' } }}
                    >
                      Create Another
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ) : (
              <Box sx={{ mt: 2 }}>
                {selectedStandard ? (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Selected Standard: {selectedStandard.id} - {selectedStandard.title}
                    </Typography>
                    {selectedCoreIdea && (
                      <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 1 }}>
                        Selected Core Idea: {selectedCoreIdea.title}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Please select a standard from the list
                  </Typography>
                )}
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="(Optional) Enter additional requirements or preferences for the worksheet here"
                  value={worksheetRequirements}
                  onChange={(e) => setWorksheetRequirements(e.target.value)}
                  sx={{ mb: 2 }}
                />
                {error && (
                  <Typography color="error" sx={{ mb: 2 }}>
                    {error}
                  </Typography>
                )}
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleGenerateWorksheet(selectedStandard, worksheetRequirements, false)}
                  disabled={!selectedStandard || isGenerating}
                  sx={{ mt: 2, bgcolor: 'black', '&:hover': { bgcolor: '#333' } }}
                >
                  {isGenerating ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} color="inherit" />
                      Generating Worksheet...
                    </Box>
                  ) : (
                    'Generate Worksheet'
                  )}
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export default WorksheetGenerator; 